import clsx from 'clsx';
import { useLanguage } from 'features/hooks/useLanguage';
import { LanguageType } from 'types';
import { ParsedEntryValue } from '../utils/formUtils';
import {
	CategoryMultipleVariable,
	CategoryVariable,
	isCategoryFixedVariable,
	isCategoryMultipleFixedVariable,
	Variable,
	VisibilityDependencyRule
} from '../types';
import { DateTime } from 'luxon';
import { isBackendFormFile, isFrontendFormFile } from '../utils/zodUtils';
import { withTimeUnits } from '../inputs/time-duration/formatMicroSeconds';
import { BackendFileFromDiff } from './BackendFileFromDiff';
import { logWithDetails } from 'sentry';

interface Props {
	className?: string;
	showDependency?: boolean;
	diffs: {
		from?: ParsedEntryValue;
		to: ParsedEntryValue;
		variable: Variable;
		becauseRules?: VisibilityDependencyRule[];
	}[];
}

export const Diffs = ({ className, diffs, showDependency }: Props) => {
	const language = useLanguage();
	const translation = DICTIONARY[language];

	return (
		<table className={clsx('w-full text-left', className)}>
			<thead>
				<tr>
					<th className="w-1/3 pb-2 text-xs font-semibold tracking-wide uppercase text-warning-700">
						{translation.columnField}
					</th>
					<th className="pb-2 text-xs font-semibold tracking-wide uppercase text-warning-700">
						{translation.columnPreviousValue}
					</th>
					<th className="pb-2 text-xs font-semibold tracking-wide uppercase text-warning-700">
						{translation.columnNewValue}
					</th>
					{showDependency && (
						<th className="pb-2 text-xs font-semibold tracking-wide uppercase text-warning-700 text-wrap">
							{translation.columnDependency}
						</th>
					)}
				</tr>
			</thead>
			<tbody>
				{diffs.map(({ from, to, variable, becauseRules }) => {
					return (
						<tr
							key={variable.variableName}
							data-testid={`diffed-field_${variable?.variableName}`}
						>
							<td className="py-2 pr-4 text-sm font-medium text-gray-800 align-top">
								{variable.variableLabel}
							</td>
							<td className="py-2 text-sm text-gray-700 align-top">
								<RenderFieldValue fieldValue={from} variable={variable} oldValue />
							</td>
							<td className="py-2 text-sm font-semibold text-gray-800 align-top">
								<RenderFieldValue fieldValue={to} variable={variable} />
							</td>
							{showDependency && (
								<td className="py-2 text-sm text-gray-800 align-top">
									{(becauseRules || []).map(rule => (
										<p
											key={`${rule.supplierVariable.variableName}-${rule.dependantVariable.variableName}-${rule.operator}-${rule.value}`}
										>
											{translation.dependencyChanged(rule)}
										</p>
									))}
								</td>
							)}
						</tr>
					);
				})}
			</tbody>
		</table>
	);
};

const RenderFieldValue = ({
	fieldValue,
	variable,
	oldValue
}: {
	fieldValue?: ParsedEntryValue;
	variable: Variable;
	oldValue?: boolean;
}) => {
	const language = useLanguage();
	const translation = DICTIONARY[language];

	if (!fieldValue) {
		return (
			<p className="italic text-error-700">
				{oldValue ? translation.noValueOld : translation.noValue}
			</p>
		);
	}

	if (Array.isArray(fieldValue)) {
		if (fieldValue.length === 0) {
			return (
				<p className="italic text-error-700">
					{oldValue ? translation.noValueOld : translation.noValue}
				</p>
			);
		}

		return (
			<ul>
				{fieldValue.map((value, idx) => (
					<li key={`${idx}-${value}`}>{value}</li>
				))}
			</ul>
		);
	}

	if (isBackendFormFile(fieldValue)) {
		return (
			<BackendFileFromDiff variableName={variable.variableName} fileId={fieldValue.fileId} />
		);
	}

	const value = formatFieldValue({ fieldValue, variable });
	if (!value) {
		return (
			<p className="italic text-error-700">
				{oldValue ? translation.noValueOld : translation.noValue}
			</p>
		);
	}

	return <>{value}</>;
};

const formatFieldValue = ({
	fieldValue,
	variable
}: {
	fieldValue?: ParsedEntryValue;
	variable: Variable;
}) => {
	if (!fieldValue) {
		return '';
	}

	if (isBackendFormFile(fieldValue)) {
		return fieldValue;
	}

	const fieldType = variableToFieldType(variable);

	if (isFrontendFormFile(fieldValue)) {
		return fieldValue.fileName;
	}

	if (fieldType === 'date') {
		return DateTime.fromISO(fieldValue as string).toLocaleString(DateTime.DATE_MED);
	}

	if (fieldType === 'datetime') {
		return DateTime.fromISO(fieldValue as string).toLocaleString(
			DateTime.DATETIME_MED_WITH_SECONDS
		);
	}

	if (fieldType === 'timeDuration') {
		if (!variable || variable.variableType !== 'timeDuration') {
			logWithDetails(new Error('Variable is required for time duration fields'), {
				fieldValue,
				fieldType
			});

			return fieldValue;
		}

		if (typeof fieldValue !== 'string') {
			logWithDetails(new Error('Field value is not a string'), {
				fieldValue,
				fieldType
			});

			return fieldValue;
		}

		return withTimeUnits({
			colonSeparatedValues: fieldValue,
			maxTimeUnit: variable.durationFormat.maxTimeUnit
		});
	}

	if (fieldType === 'category') {
		if (!variable) {
			logWithDetails(new Error('Variable is required for category fields'), {
				fieldValue,
				fieldType
			});

			return fieldValue;
		}

		if (!isCategoryVariable(variable)) {
			logWithDetails(new Error(`Variable type is not supported`), {
				variableType: variable.variableType
			});
			return fieldValue;
		}

		if (isCategoryFixedVariable(variable) || isCategoryMultipleFixedVariable(variable)) {
			const category = variable.allowedCategories.find(
				category => category.value === fieldValue
			);

			if (!category) {
				return fieldValue;
			}
			return category.label;
		}

		// e.g. when a category is added front end we don't have the label, so we use the added value
		return fieldValue;
	}

	return fieldValue;
};

export type FieldType =
	| 'date'
	| 'datetime'
	| 'frontend-file'
	| 'backend-file'
	| 'category'
	| 'timeDuration'
	| 'text';
export const variableToFieldType = (variable: Variable): FieldType => {
	const isDate = variable?.variableType === 'date';
	if (isDate) {
		return 'date';
	}

	const isDateTime = variable?.variableType === 'datetime';
	if (isDateTime) {
		return 'datetime';
	}

	const isFile = variable?.variableType === 'file';
	if (isFile) {
		return 'backend-file';
	}

	if (variable && isCategoryVariable(variable)) {
		return 'category';
	}

	if (variable && variable.variableType === 'timeDuration') {
		return 'timeDuration';
	}

	return 'text';
};

const isCategoryVariable = (
	variable: Variable
): variable is CategoryVariable | CategoryMultipleVariable => {
	return ['category', 'categoryMultiple'].includes(variable.variableType);
};

const DICTIONARY = {
	[LanguageType.English]: {
		columnField: 'Field',
		columnPreviousValue: 'Previous value',
		columnNewValue: 'New value',
		columnDependency: 'Why',
		noValue: 'Deleted',
		noValueOld: 'No value',
		operator: (operator: string) => {
			switch (operator) {
				case '>=':
					return 'bigger than or equal to';
				case '<=':
					return 'less than or equal to';
				case '>':
					return 'bigger than';
				case '<':
					return 'less than';
				case '==':
					return 'equal to';
			}

			return operator;
		},
		dependencyChanged: (d: VisibilityDependencyRule) =>
			`${d.dependantVariable.variableLabel} can only be displayed if ${
				d.supplierVariable.variableLabel
			} is ${DICTIONARY['EN'].operator(d.operator)} ${d.value}`
	},

	[LanguageType.Norwegian]: {
		columnField: 'Felt',
		columnPreviousValue: 'Tidligere verdi',
		columnNewValue: 'Ny verdi',
		columnDependency: 'Hvorfor',
		noValue: 'Fjernet',
		noValueOld: 'Tomt',
		operator: (operator: string) => {
			switch (operator) {
				case '>=':
					return 'større eller lik';
				case '<=':
					return 'mindre eller lik';
				case '>':
					return 'større enn';
				case '<':
					return 'mindre enn';
				case '==':
					return 'lik';
			}

			return operator;
		},
		dependencyChanged: (d: VisibilityDependencyRule) =>
			`${d.dependantVariable.variableLabel} kan bare vises hvis ${
				d.supplierVariable.variableLabel
			} er ${DICTIONARY['NB'].operator(d.operator)} ${d.value}`
	}
};
